import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/lib/roles";
import OutboxProtectionPanel from "@/components/OutboxProtectionPanel";
import MetaRegisterCard from "@/components/MetaRegisterCard";
import MetaTemplatesSyncCard from "@/components/MetaTemplatesSyncCard";
import {
  MessageCircle, CheckCircle2, AlertCircle, ShieldAlert,
  Loader2, ArrowRight, RefreshCw, Info, PlugZap, PowerOff, QrCode, X,
} from "lucide-react";

// ---------- Status presets ----------
// Kept broad so callers (SuperadminOrganizations, older components) that
// pass raw internal statuses continue to render sensibly. Session A adds
// the public statuses (`not_connected`, `attention_required`, ...).
const STATUS_PRESETS = {
  not_configured:      { label: "Não ligado",           color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: AlertCircle,  intent: "neutral" },
  not_connected:       { label: "Não ligado",           color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: AlertCircle,  intent: "neutral" },
  onboarding:          { label: "Em onboarding",        color: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertCircle, intent: "wip" },
  pending_meta:        { label: "A aguardar Meta",      color: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertCircle, intent: "wip" },
  connected:           { label: "Ligado",               color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, intent: "ok" },
  disabled:            { label: "Desligado",            color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: PowerOff,     intent: "neutral" },
  disconnected:        { label: "Desligado",            color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: PowerOff,     intent: "neutral" },
  error:               { label: "Atenção necessária",   color: "bg-amber-50 text-amber-900 border-amber-200", icon: ShieldAlert, intent: "warn" },
  attention_required:  { label: "Atenção necessária",   color: "bg-amber-50 text-amber-900 border-amber-200", icon: ShieldAlert, intent: "warn" },
  reconnect_required:  { label: "Reconectar",           color: "bg-red-50 text-red-800 border-red-200",       icon: RefreshCw,   intent: "bad" },
};

export function WhatsappStatusPill({ status, className = "" }) {
  const p = STATUS_PRESETS[status] || STATUS_PRESETS.not_configured;
  const Icon = p.icon;
  return (
    <span data-testid={`wa-status-pill-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${p.color} ${className}`}>
      <Icon className="w-3.5 h-3.5" /> {p.label}
    </span>
  );
}

// ---------- Simplified tenant WhatsApp page ----------
export default function WhatsappSettings() {
  const { isAdmin } = useRole();
  const [health, setHealth] = useState(null);
  const [signup, setSignup] = useState({ enabled: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [zapiQrOpen, setZapiQrOpen] = useState(false);

  const refresh = async () => {
    setLoading(true); setErr("");
    try {
      const [{ data: h }, { data: cfg }] = await Promise.all([
        api.get("/organizations/me/whatsapp/health"),
        api.get("/organizations/me/whatsapp/embedded-signup-config").catch(() => ({ data: { enabled: false } })),
      ]);
      setHealth(h); setSignup(cfg);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Falha ao carregar estado do WhatsApp.");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const startConnect = async () => {
    // Provider-aware: Z-API uses QR modal, Meta/Twilio show the placeholder.
    if (health?.provider === "z_api") {
      setZapiQrOpen(true);
      return;
    }
    if (!signup?.enabled) {
      // Session A: Embedded Signup v4 not yet implemented on this install.
      // Copy avoids the loaded "approval" framing and points to the
      // operational fallback (contact Do IT Happen support).
      setErr("Liga o WhatsApp Business da tua agência à PromoCrew. A ligação automática está a ser preparada. Para configurar já o WhatsApp, contacta o suporte Do IT Happen.");
      return;
    }
    // Placeholder for Session B (Embedded Signup v4 kickoff via FB JS SDK).
    setErr("Fluxo Embedded Signup ficará disponível em breve. Estamos a preparar a integração oficial Meta.");
  };

  const disconnect = async () => {
    if (!window.confirm("Tens a certeza que queres desligar o WhatsApp? Não perdes histórico nem promotores.")) return;
    setBusy(true); setErr("");
    try {
      if (health?.provider === "z_api") {
        await api.post("/organizations/me/z-api/disconnect");
      } else {
        await api.post("/organizations/me/whatsapp/disconnect");
      }
      await refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Falha ao desligar.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="text-sm text-zinc-500 flex items-center gap-2" data-testid="wa-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
    </div>;
  }

  const status = health?.active_status || health?.status || "not_connected";
  const preset = STATUS_PRESETS[status] || STATUS_PRESETS.not_connected;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="wa-settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">WhatsApp</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Liga o WhatsApp Business da tua agência para enviar convites diretamente através da PromoCrew.
        </p>
      </div>

      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="wa-error">{err}</div>
      )}

      {/* State card */}
      <div
        data-testid={`wa-state-card-${status}`}
        className={`rounded-2xl border p-6 shadow-sm bg-white ${status === "connected" ? "border-emerald-200" : status === "reconnect_required" ? "border-red-200" : status === "attention_required" ? "border-amber-200" : "border-zinc-200"}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${status === "connected" ? "bg-emerald-600 text-white" : "bg-zinc-950 text-white"}`}>
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <WhatsappStatusPill status={status} />
              {status === "connected" && (
                <>
                  <div className="mt-2 font-display text-xl font-semibold text-zinc-950" data-testid="wa-display-name">
                    {health.display_name || "WhatsApp Business"}
                  </div>
                  {health.display_phone_number && (
                    <div className="text-sm text-zinc-500 mt-0.5" data-testid="wa-display-phone">{health.display_phone_number}</div>
                  )}
                  {health.provider === "twilio" && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[11px] font-semibold text-indigo-800" data-testid="wa-provider-badge">
                      via Twilio {health.connection_mode === "twilio_sandbox" ? "Sandbox" : "Cloud"}
                    </div>
                  )}
                  {health.provider === "z_api" && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-900" data-testid="wa-provider-badge-zapi">
                      via Z-API (QR)
                    </div>
                  )}
                  <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
                    O WhatsApp está pronto para enviar convites pela PromoCrew.
                  </p>
                  {health.provider === "twilio" && health.connection_mode === "twilio_sandbox" && health.sandbox_keyword && (
                    <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="wa-sandbox-hint">
                      <strong>Modo Sandbox:</strong> os destinatários têm de enviar <code className="font-mono">join {health.sandbox_keyword}</code> ao número Twilio antes de receberem mensagens.
                    </p>
                  )}
                </>
              )}
              {status === "not_connected" && (
                <>
                  <div className="mt-2 font-display text-xl font-semibold text-zinc-950">Ainda por ligar</div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                    Podes gerir promotores e criar eventos normalmente.
                    Só vais precisar de ligar o WhatsApp quando quiseres enviar convites por mensagem.
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Já usas WhatsApp Business? Podes continuar a usar o teu número normalmente.
                  </p>
                </>
              )}
              {status === "attention_required" && (
                <>
                  <div className="mt-2 font-display text-xl font-semibold text-zinc-950">Atenção necessária</div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                    Detetámos um problema com a ligação ao WhatsApp. Verifica os detalhes ou volta a ligar.
                  </p>
                </>
              )}
              {status === "reconnect_required" && (
                <>
                  <div className="mt-2 font-display text-xl font-semibold text-zinc-950">Reconectar WhatsApp</div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                    A ligação ao WhatsApp precisa de ser renovada.
                  </p>
                </>
              )}
              {status === "disconnected" && (
                <>
                  <div className="mt-2 font-display text-xl font-semibold text-zinc-950">WhatsApp desligado</div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                    A ligação foi desligada. Podes voltar a ligar quando quiseres retomar os envios.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-5 mt-5 border-t border-zinc-100">
          {isAdmin && (status === "not_connected" || status === "disconnected") && (
            <button
              onClick={startConnect}
              data-testid="wa-connect-btn"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
            >
              <PlugZap className="w-4 h-4" /> Ligar WhatsApp <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {isAdmin && status === "attention_required" && (
            <button
              onClick={refresh}
              data-testid="wa-recheck-btn"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
            >
              <RefreshCw className="w-4 h-4" /> Verificar ligação
            </button>
          )}
          {isAdmin && status === "reconnect_required" && (
            <button
              onClick={startConnect}
              data-testid="wa-reconnect-btn"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
            >
              <RefreshCw className="w-4 h-4" /> Reconectar
            </button>
          )}
          {isAdmin && status === "connected" && (
            <button
              onClick={disconnect}
              disabled={busy}
              data-testid="wa-disconnect-btn"
              className="inline-flex items-center gap-2 border border-zinc-200 hover:bg-zinc-50 px-4 py-2.5 rounded-lg text-sm font-medium text-zinc-700 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PowerOff className="w-4 h-4" />}
              Desligar
            </button>
          )}
        </div>
      </div>

      {/* Info panel — not_connected + Embedded Signup not enabled yet + not Z-API */}
      {status === "not_connected" && !signup?.enabled && health?.provider !== "z_api" && (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-4 flex items-start gap-3 text-sm text-zinc-600" data-testid="wa-brevemente">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-zinc-900 mb-0.5">Ligação automática em preparação</div>
            A auto-configuração via Meta ainda está a ser preparada na PromoCrew.
            Para configurar já o WhatsApp da tua agência, contacta o suporte Do IT Happen.
          </div>
        </div>
      )}

      {zapiQrOpen && (
        <ZapiQrModal onClose={() => setZapiQrOpen(false)} onConnected={async () => {
          setZapiQrOpen(false);
          await refresh();
        }} />
      )}

      <MetaRegisterCard />
      <MetaTemplatesSyncCard />
      <OutboxProtectionPanel />
    </div>
  );
}


// ---------- Z-API QR Modal (WhatsApp Web onboarding) ----------
// UX: first fetch immediate → refresh every 10s → max 3 auto-refreshes →
// stop and show "Gerar novo QR" button. When status polling reports
// connected=true, close immediately.
function ZapiQrModal({ onClose, onConnected }) {
  const [qr, setQr] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | waiting | expired | connected | error
  const [errMsg, setErrMsg] = useState("");
  const abortRef = useRef(false);
  const qrShownRef = useRef(false); // only count "connected" AFTER we've shown a fresh QR

  useEffect(() => {
    abortRef.current = false;
    qrShownRef.current = false;
    fetchQr(1, true); // force: drop any old session so a NEW number can be linked
    const statusPoll = setInterval(async () => {
      if (abortRef.current) return;
      try {
        const r = await api.get("/organizations/me/z-api/status");
        // Ignore the pre-existing (old-number) session — only celebrate a scan
        // that happened AFTER we displayed a fresh QR.
        if (r.data?.connected && qrShownRef.current) {
          setStatus("connected");
          abortRef.current = true;
          setTimeout(() => onConnected(), 900);
        }
      } catch { /* silent */ }
    }, 4000);
    return () => {
      abortRef.current = true;
      clearInterval(statusPoll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQr = async (attemptNo, force = false) => {
    setStatus("loading");
    setErrMsg("");
    try {
      const r = await api.get(`/organizations/me/z-api/qr${force ? "?force=true" : ""}`);
      if (abortRef.current) return;
      if (r.data?.still_connected) {
        setStatus("error");
        setErrMsg(r.data?.error || "O número antigo continua ligado. Remove o aparelho ligado no telemóvel e tenta de novo.");
        return;
      }
      if (r.data?.connected) {
        // Only treat as success if a fresh QR was already shown (i.e. a new scan).
        if (qrShownRef.current) {
          setStatus("connected");
          setTimeout(() => onConnected(), 900);
        } else {
          setStatus("error");
          setErrMsg("O número atual continua ligado. Carrega em 'Desligar e gerar novo QR' para ligar outro número.");
        }
        return;
      }
      const value = r.data?.value || r.data?.qr;
      if (!value) {
        setStatus("error");
        setErrMsg(r.data?.error || "Não foi possível obter QR Code.");
        return;
      }
      setQr(value);
      qrShownRef.current = true;
      setStatus("waiting");
      setAttempts(attemptNo);
      // Auto-refresh (max 3) after ~10s
      if (attemptNo < 3) {
        setTimeout(() => { if (!abortRef.current) fetchQr(attemptNo + 1); }, 10000);
      } else {
        setTimeout(() => { if (!abortRef.current) setStatus("expired"); }, 10000);
      }
    } catch (e) {
      if (abortRef.current) return;
      setStatus("error");
      setErrMsg(formatApiError(e.response?.data?.detail) || "Erro ao obter QR Code.");
    }
  };

  const regenerate = () => { setAttempts(0); setQr(null); qrShownRef.current = false; fetchQr(1, true); };

  // Build image src — Z-API returns raw base64 or a data: URL.
  const qrSrc = qr
    ? (qr.startsWith("data:") ? qr : `data:image/png;base64,${qr}`)
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="zapi-qr-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button onClick={onClose} data-testid="zapi-qr-close"
                className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-800">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <QrCode className="w-5 h-5 text-zinc-700" />
          <h3 className="text-lg font-display font-semibold text-zinc-950">Ligar WhatsApp por QR Code</h3>
        </div>
        <p className="text-sm text-zinc-500 mb-5">
          Abre WhatsApp no telemóvel → <strong>Definições → Dispositivos ligados → Ligar um dispositivo</strong>.
          Para <strong>trocar de número</strong>, lê este QR com o WhatsApp do <strong>número novo</strong>.
        </p>

        <div className="flex flex-col items-center gap-3 min-h-[260px] justify-center">
          {status === "loading" && (
            <div className="text-zinc-500 flex items-center gap-2" data-testid="zapi-qr-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> A obter QR…
            </div>
          )}
          {status === "waiting" && qrSrc && (
            <>
              <img src={qrSrc} alt="QR Code" className="w-56 h-56 rounded-lg border border-zinc-200"
                   data-testid="zapi-qr-image" />
              <div className="text-xs text-zinc-500">Tentativa {attempts}/3 — actualiza automaticamente</div>
            </>
          )}
          {status === "expired" && (
            <div className="text-center space-y-3" data-testid="zapi-qr-expired">
              <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                QR Code expirou depois de 3 tentativas. Gera um novo abaixo.
              </div>
              <button onClick={regenerate} data-testid="zapi-qr-regenerate"
                      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                <RefreshCw className="w-4 h-4" /> Gerar novo QR Code
              </button>
            </div>
          )}
          {status === "error" && (
            <div className="text-center space-y-3" data-testid="zapi-qr-error">
              <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {errMsg}
              </div>
              <button onClick={regenerate} className="text-sm underline text-zinc-700">Tentar novamente</button>
            </div>
          )}
          {status === "connected" && (
            <div className="text-center space-y-2" data-testid="zapi-qr-connected">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <div className="font-semibold text-emerald-800">WhatsApp ligado!</div>
              <div className="text-xs text-zinc-500">A fechar…</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Retained for backwards-compat: some pages imported this banner. Now that
// EventDetail uses the WhatsappGateModal directly, this banner is essentially
// a no-op placeholder. Kept exported so any external references don't crash.
export function WhatsappGateBanner() { return null; }
